import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { TransportInvoiceDetailModel } from 'src/app/models/additional/transport-invoice-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { TransportInvoiceDetailCaptureTableModel } from 'src/app/models/tables/additional/transport-invoice-detail-capture-table.model';
import { TransportInvoiceDetailService } from 'src/app/services/additional/transport-invoice-detail.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-transport-invoice-detail',
  templateUrl: './transport-invoice-detail.component.html',
  styleUrls: ['./transport-invoice-detail.component.scss'],
  standalone: false
})
export class TransportInvoiceDetailComponent implements OnInit, OnChanges {

  @ViewChild('documentSelection') documentSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() disabledDetail: boolean = false;
  @Input() transportInvoiceId: number = 0;
  @Input() providerId: number = 0;
  @Input() permissionUpdatable: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();

  ascendant: boolean = true;
  ascendantDocument: boolean = true;
  columnSort: string | null = null;
  columnSortDocument: string | null = null;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  dataDocument: PagedResponseModel<TransportInvoiceDetailCaptureTableModel> = new PagedResponseModel<TransportInvoiceDetailCaptureTableModel>();
  detailData: PagedResponseModel<TransportInvoiceDetailCaptureTableModel> = new PagedResponseModel<TransportInvoiceDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = true;
  filtersDocument: ColumnResponseModel[] = [];
  headerDocument: ColumnResponseModel[] = [];
  inputsForDocumentSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: [],
  });
  isFormView: boolean = false;
  transportInvoiceDetail: TransportInvoiceDetailModel = new TransportInvoiceDetailModel();
  limit: number = 10;
  limitDocument: number = 10;
  output$ = new Subject<any>();
  page: number = 1;
  pageDocument: number = 1;
  subscription?: Subscription;
  tableActions: TableAction[] = [];
  componentTableSelection = CustomTableSelectComponent;

  constructor(
    private confirmDialogService: ConfirmDialogService,
    private transportInvoiceDetailService: TransportInvoiceDetailService,
    private toastHandlerService: ToastHandlerService,
  ) { }

  async ngOnInit(): Promise<void> {
    await this.getColumns();
    await this.getTransportInvoiceDetails();
    this.tableActions = this.getDefaultActions();

    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeDocument(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeDocument(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const transportInvoiceId = changes['transportInvoiceId'];
    if (transportInvoiceId && !transportInvoiceId.firstChange) {
      this.getFormView(false);
      this.getTransportInvoiceDetails();
      this.disabledButton = this.transportInvoiceId === 0 || this.disabledDetail;
      this.clearSelectionTrigger++;
    }

    const disabledDetail = changes['disabledDetail'];
    if (disabledDetail && !disabledDetail.firstChange) {
      this.tableActions = this.getDefaultActions();
      this.disabledButton = this.transportInvoiceId === 0 || this.disabledDetail;
    }
  }

  async deleteDetails(detailsSelected: any) {
    if (detailsSelected.length === 0) return;

    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar los documentos seleccionados del detalle?')) {
      try {
        await firstValueFrom(this.transportInvoiceDetailService.deleteSomeDetails(this.transportInvoiceId, detailsSelected));
        this.toastHandlerService.showToast(
          'success',
          'Los documentos fueron eliminados del detalle exitosamente.'
        );
        this.transportInvoiceDetail = new TransportInvoiceDetailModel();
        this.clearSelectionTrigger++;
        this.getTransportInvoiceDetails();
      } catch (error) {
        console.error('Error al eliminar los documentos del detalle.', error);
      }
    }
  }

  async handleSelectedItems(selectedItems: any[]) {
    if (!selectedItems || selectedItems.length === 0) return;

    const detailsToCreate: TransportInvoiceDetailModel[] = selectedItems.map(item => {
      const detail = new TransportInvoiceDetailModel();
      detail.transportInvoiceId = this.transportInvoiceId;
      detail.documentId = item.documentId || item.id;
      detail.document = item.document;
      detail.documentType = item.documentType;
      detail.consecutive = item.consecutive;
      detail.dateDocument = item.dateDocument;
      detail.transportValuePay = item.transportValuePay;
      // Asignar Vr. Acordado por defecto como Vr. Facturado
      detail.transportValueInvoiced = item.transportValuePay;
      return detail;
    });

    if (selectedItems.length > 1) {
      try {
        await firstValueFrom(this.transportInvoiceDetailService.addDocumentsToTransporInvoice(this.transportInvoiceId, detailsToCreate));
        this.toastHandlerService.showToast('success', 'Documentos agregados al detalle exitosamente.');
        this.getTransportInvoiceDetails();
      } catch (error) {
        console.error('Error al agregar documentos al detalle.', error);
      }
    } else {
      this.transportInvoiceDetail = detailsToCreate[0];
      this.getFormView(true);
    }

  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.transportInvoiceDetailService.getColumnsForDetail());
    } catch (error) {
      console.error('Error al obtener columnas de detalle.', error);
    }
  }

  async getColumnsSelection() {
    try {
      this.headerDocument = await firstValueFrom(this.transportInvoiceDetailService.getColumnsForSelection());
    } catch (error) {
      console.error('Error al obtener columnas para selección de documentos.', error);
    }
  }

  async getTransportInvoiceDetails() {
    if (!this.transportInvoiceId || this.transportInvoiceId === 0) {
      this.detailData = new PagedResponseModel<TransportInvoiceDetailCaptureTableModel>();
      this.emitQuantityDetails.emit(0);
      return;
    }
    try {
      this.detailData = await firstValueFrom(this.transportInvoiceDetailService.getTransportInvoicesDetailWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.transportInvoiceId
      ));
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al obtener el detalle de la factura de transporte.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.disabledDetail) {
      return [];
    } else {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    }
  }

  async getDocumentsSelection() {
    if (!this.providerId || this.providerId === 0) return;
    try {
      this.dataDocument = await firstValueFrom(this.transportInvoiceDetailService.getDocumentsSelectionWithPagination(
        this.pageDocument, this.limitDocument, this.columnSortDocument, this.ascendantDocument,
        this.filtersDocument, this.providerId
      ));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener lista de documentos para selección.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async onAction(event: { action: string, item?: TransportInvoiceDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      const detail = new TransportInvoiceDetailModel();
      detail.id = event.item.id;
      detail.transportInvoiceId = this.transportInvoiceId;
      detail.documentId = event.item.documentId;
      detail.document = event.item.document;
      detail.documentType = event.item.documentType;
      detail.consecutive = event.item.consecutive;
      detail.dateDocument = event.item.dateDocument;
      detail.transportValuePay = event.item.transportValuePay;
      detail.transportValueInvoiced = event.item.transportValueInvoiced;
      this.transportInvoiceDetail = detail;
      this.getFormView(true);
    }
    if (event.action === 'bulk-delete' && event.item) {
      this.deleteDetails(event.item);
    }
  }

  async onFilterChangeDocument(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersDocument = this.removeDuplicates(filters);
    this.filtersDocument.forEach((filter: any) => this.processFilter(filter));
    this.getDocumentsSelection();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getTransportInvoiceDetails();
  }

  async onPageChangeDocument(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageDocument = event.page;
    this.limitDocument = event.pageSize;
    this.columnSortDocument = event.sortField || null;
    this.ascendantDocument = event.sortOrder === 1;
    this.getDocumentsSelection();
  }

  async openDocumentSelectionModal() {
    this.filtersDocument = [];
    await this.getColumnsSelection();
    await this.getDocumentsSelection();
    this.documentSelectionModalComponent && this.documentSelectionModalComponent.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersDocument = this.filtersDocument.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersDocument.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForDocumentSelection$.next({
      headers: this.headerDocument,
      data: this.dataDocument,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersDocument,
      actions: []
    });
  }
}
